import { Activity, Bot, CalendarClock, Coins, Send, Users, Wrench } from "lucide-react"
import * as React from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { RunStatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AdminRecentRun, AdminStats } from "@/lib/types"
import { formatDateTime, formatDuration } from "@/lib/utils"

const n = (value: number) => value.toLocaleString()

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  hint?: string
  title?: string
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="bg-muted text-muted-foreground rounded-md p-2">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium">{label}</div>
          <div className="text-2xl font-semibold leading-tight" title={title}>
            {value}
          </div>
          {hint && <div className="text-muted-foreground mt-0.5 text-xs">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  )
}

export function AdminPage() {
  const navigate = useNavigate()
  const { locale } = useLocale()
  const [stats, setStats] = React.useState<AdminStats | null>(null)
  const [runs, setRuns] = React.useState<AdminRecentRun[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    Promise.all([api.adminStats(), api.adminRecentRuns(50)])
      .then(([s, r]) => {
        setStats(s)
        setRuns(r)
      })
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load admin data."),
      )
      .finally(() => setLoading(false))
  }, [])

  const byStatus = stats ? Object.entries(stats.runs.by_status).sort() : []

  return (
    <>
      <PageHeader
        title="Admin"
        description="Platform activity across every account."
        action={
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/users">Manage users</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/spend">Spending</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/stt">STT usage</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/expenses">Expenses</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/debts">Debts</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/ops">Config / Ops</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/audit">Audit log</Link>
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-6">
        {loading || !stats ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard
                icon={Users}
                label="Users"
                value={n(stats.users.total)}
                hint={`${n(stats.users.active)} active · ${n(stats.users.new_7d)} new this week`}
              />
              <StatCard icon={Bot} label="Agents" value={n(stats.agents.total)} hint="not deleted" />
              <StatCard
                icon={Activity}
                label="Runs"
                value={n(stats.runs.total)}
                hint={`${n(stats.runs.last_24h)} today · ${n(stats.runs.failed_24h)} failed today`}
              />
              <StatCard
                icon={Coins}
                label="Tokens used"
                value={n(stats.tokens.total)}
                title={`${n(stats.tokens.total)} tokens total`}
                hint={`${n(stats.tokens.last_24h)} today · ${n(stats.tokens.last_7d)} this week`}
              />
              <StatCard
                icon={Wrench}
                label="Tool calls"
                value={n(stats.tool_calls.total)}
                hint={`${n(stats.tool_calls.last_24h)} today`}
              />
              <StatCard
                icon={CalendarClock}
                label="Active schedules"
                value={n(stats.schedules.active)}
              />
              <StatCard
                icon={Send}
                label="Telegram bots"
                value={n(stats.telegram.bots)}
                hint={
                  stats.telegram.bots === 1 ? "1 connected" : `${n(stats.telegram.bots)} connected`
                }
              />
            </div>

            {byStatus.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-xs font-medium">Runs by status</span>
                {byStatus.map(([status, count]) => (
                  <span
                    key={status}
                    className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1"
                  >
                    <RunStatusBadge status={status as never} />
                    <span className="text-xs font-medium tabular-nums">{n(count)}</span>
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        <div>
          <h2 className="mb-3 text-sm font-semibold">Recent runs (all users)</h2>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : runs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No runs yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Status</th>
                    <th className="px-4 py-2 text-left font-medium">Owner</th>
                    <th className="px-4 py-2 text-left font-medium">Agent</th>
                    <th className="px-4 py-2 text-left font-medium">Tokens</th>
                    <th className="px-4 py-2 text-left font-medium">Duration</th>
                    <th className="px-4 py-2 text-left font-medium">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      className="hover:bg-accent/40 cursor-pointer border-t"
                      onClick={() => navigate(`/runs/${run.id}`)}
                    >
                      <td className="px-4 py-2">
                        <RunStatusBadge status={run.status as never} />
                      </td>
                      <td className="px-4 py-2">{run.owner_email}</td>
                      <td className="px-4 py-2">{run.agent_name ?? "—"}</td>
                      <td className="px-4 py-2">{run.total_tokens.toLocaleString()}</td>
                      <td className="px-4 py-2">{formatDuration(run.duration_ms)}</td>
                      <td className="text-muted-foreground px-4 py-2">
                        {formatDateTime(run.started_at, locale)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
